import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const TEMPLATES = [
  ["confirmation", "Booking confirmation", "e.g. Please arrive 5 minutes early. Parking is at the rear of the building."],
  ["reminder", "Reminder (the day before)", "e.g. Remember to come with clean, dry hair."],
  ["cancellation", "Cancellation", "e.g. We're sorry to miss you. Rebook any time on our booking page."],
];

export function EmailPolicySettings({ business, onSaved }) {
  const [tpl, setTpl] = useState({ confirmation: "", reminder: "", cancellation: "", ...(business.email_templates || {}) });
  const [policy, setPolicy] = useState({ enabled: false, text: "", notice_hours: 24, ...(business.cancellation_policy || {}) });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (policy.enabled && !policy.text.trim()) { toast.error("Write your cancellation policy, or switch it off"); return; }
    setSaving(true);
    try {
      await api.put("/business", { email_templates: tpl, cancellation_policy: { ...policy, notice_hours: Math.min(168, Math.max(0, Number(policy.notice_hours) || 0)) } });
      await onSaved(); toast.success("Emails & policy saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-stone-200 p-5 sm:p-6 shadow-sm space-y-4" data-testid="email-templates-card">
        <div><h2 className="font-semibold text-lg">Client emails</h2>
          <p className="text-sm text-stone-500">Your message is added to our standard emails. To add instructions for one treatment, edit it in Services.</p></div>
        {TEMPLATES.map(([k, label, ph]) => (
          <div key={k}><Label>{label}</Label>
            <Textarea data-testid={`email-template-${k}`} value={tpl[k]} maxLength={2000} rows={3} placeholder={ph}
              onChange={(e) => setTpl((t) => ({ ...t, [k]: e.target.value }))} className="mt-1.5 rounded-lg" /></div>
        ))}
      </div>
      <div className="bg-white rounded-2xl border border-stone-200 p-5 sm:p-6 shadow-sm space-y-4" data-testid="cancellation-policy-card">
        <div className="flex items-start justify-between gap-4">
          <div><h2 className="font-semibold text-lg">Cancellation policy</h2>
            <p className="text-sm text-stone-500">Clients must tick to accept it before they can book online.</p></div>
          <Switch data-testid="policy-enabled" checked={policy.enabled} onCheckedChange={(v) => setPolicy((p) => ({ ...p, enabled: v }))} />
        </div>
        {policy.enabled && (
          <>
            <div><Label>Your policy</Label>
              <Textarea data-testid="policy-text" value={policy.text} maxLength={3000} rows={4} onChange={(e) => setPolicy((p) => ({ ...p, text: e.target.value }))}
                placeholder="e.g. Please give at least 24 hours notice to cancel or move your appointment. Deposits are non-refundable for late cancellations or no-shows." className="mt-1.5 rounded-lg" /></div>
            <div><Label>Notice needed to cancel online (hours)</Label>
              <Input type="number" min="0" max="168" data-testid="policy-notice-hours" value={policy.notice_hours} onChange={(e) => setPolicy((p) => ({ ...p, notice_hours: e.target.value }))} className="mt-1.5 h-11 rounded-lg w-32" />
              <p className="text-xs text-stone-500 mt-1">Inside this window, clients can't cancel from their account and are asked to call you.</p></div>
          </>
        )}
      </div>
      <Button data-testid="save-email-policy" onClick={save} disabled={saving} className="rounded-full h-11 px-6 bg-stone-900 hover:bg-stone-800">{saving ? "Saving…" : "Save emails & policy"}</Button>
    </div>
  );
}
